import * as React from 'react';

import { Portal } from '~shared/components/Portal';
import { normalizeKey } from '~shared/utils/normalize-keycode';

import { ImageZoomProps } from './ImageZoom';
import { getZoomPopupStyle, mockElement } from './utils';

const DURATION = 300;

interface Props extends Pick<ImageZoomProps, 'closeLabel'> {
  zoomSrc: string;
  onLoad: () => void;
  onUnload: () => void;
  parentRef: React.RefObject<HTMLDivElement>;
}

export function ImageZoomPopup({
  zoomSrc,
  closeLabel,
  onLoad,
  onUnload,
  parentRef,
}: Props): React.ReactElement {
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isUnloading, setIsUnloading] = React.useState(false);
  const [intrinsicDimensions, setIntrinsicDimensions] = React.useState({
    naturalWidth: 0,
    naturalHeight: 0,
  });

  const closePopup = React.useCallback((e) => {
    e.preventDefault();
    setIsUnloading(true);
  }, []);

  const handleKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const targetKey = normalizeKey(event);

    if (targetKey === 'Escape') {
      closePopup(event);
    }
  };

  // Wait until large image is in the DOM before doing stuff.
  const handleImageLoaded = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;

    setIntrinsicDimensions({
      naturalWidth,
      naturalHeight,
    });
    setIsLoaded(true);
    onLoad();
    buttonRef.current?.focus({ preventScroll: true });
  };

  React.useEffect(() => {
    const unloadTimeout = isUnloading ? setTimeout(onUnload, DURATION) : null;

    return (): void => {
      if (unloadTimeout) {
        clearTimeout(unloadTimeout);
      }
    };
  }, [isUnloading, onUnload]);

  // Use parent element or fake one if it's not yet loaded.
  const parentEl = parentRef.current || mockElement;
  const { height, left, top, width } = parentEl.getBoundingClientRect();

  // Get physical image dimensions.
  const { naturalWidth, naturalHeight } = intrinsicDimensions;

  const popupStyle = getZoomPopupStyle({
    thumbnailWidth: width,
    thumbnailHeight: height,
    thumbnailTop: top,
    thumbnailLeft: left,
    naturalWidth,
    naturalHeight,
    zoomMargin: 24,
    duration: DURATION,
    isLoaded,
    isUnloading,
    originalTransform: parentEl.style.transform,
  });

  return (
    <Portal>
      <div
        className="fixed inset-0 z-100"
        role="dialog"
        aria-modal
        onKeyUp={handleKeyUp}
      >
        <div
          className={`absolute inset-0 bg-grey-50 transition-opacity duration-300 ${
            isUnloading ? 'opacity-0' : 'opacity-66'
          }`}
        />
        <div
          className="absolute transition-transform origin-center"
          style={popupStyle}
        >
          <img
            className="max-w-full h-auto"
            src={zoomSrc}
            alt=""
            onLoad={handleImageLoaded}
          />
        </div>
        <button
          className="absolute inset-0 w-full h-full m-0 bg-transparent border-none appearance-none outline-none cursor-zoom-out"
          aria-label={closeLabel}
          onClick={closePopup}
          ref={buttonRef}
        />
      </div>
    </Portal>
  );
}
